/**
 * 客户端数据：同步下来的全部实体（id → 实体）+ seq + 日程展开缓存（"孩子ID|日期"）+ 家庭 / 设备信息。
 * 数据量是一个家庭的规模，整体放内存，变化后 500ms 防抖写入 IndexedDB。
 * 页面用 useStore(selector) 订阅；状态是不可变的，每次修改都生成新对象。
 */
import { useRef, useSyncExternalStore } from "react";
import { idb } from "./idb";
import type {
  AchievementRule,
  AgendaDay,
  AgendaItem,
  Asset,
  Assignment,
  Card,
  CardSeries,
  Child,
  ChildCard,
  Content,
  Device,
  Family,
  FamilyMember,
  Folder,
  Parent,
  PointLedgerEntry,
  Redemption,
  Checkin,
  ReminderLog,
  ReminderRule,
  Reward,
  ScheduleEvent,
  SyncResponse,
  Tag,
} from "./models";
import { checkinKeyOf, isContentItem, isDone, isTodo } from "./models";

// ---------- 通用的小 store ----------

export interface Store<S> {
  getState(): S;
  setState(next: Partial<S> | ((s: S) => Partial<S>)): void;
  subscribe(fn: () => void): () => void;
}

export function createStore<S extends object>(initial: S): Store<S> {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    getState: () => state,
    setState(next) {
      const patch = typeof next === "function" ? next(state) : next;
      state = { ...state, ...patch };
      for (const fn of listeners) fn();
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

/** 浅比较（数组逐项、对象逐键），给返回新数组 / 新对象的 selector 用 */
export function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (!Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
  return true;
}

/**
 * 订阅 store 的一部分。selector 返回新数组 / 对象时传 shallowEqual，避免无意义的重渲染。
 * 状态没变时直接返回上次的结果（useSyncExternalStore 要求快照稳定）。
 */
export function useStoreOf<S, T>(store: Store<S>, selector: (s: S) => T, isEqual: (a: T, b: T) => boolean = Object.is): T {
  const ref = useRef<{ state: S; selector: (s: S) => T; value: T } | null>(null);
  const getSnapshot = () => {
    const s = store.getState();
    const prev = ref.current;
    if (prev && prev.state === s && prev.selector === selector) return prev.value;
    const v = selector(s);
    const value = prev && isEqual(prev.value, v) ? prev.value : v;
    ref.current = { state: s, selector, value };
    return value;
  };
  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

// ---------- 家庭数据 ----------

export interface Entities {
  children: Record<string, Child>;
  scheduleEvents: Record<string, ScheduleEvent>;
  reminderRules: Record<string, ReminderRule>;
  reminderLogs: Record<string, ReminderLog>;
  folders: Record<string, Folder>;
  tags: Record<string, Tag>;
  contents: Record<string, Content>;
  assets: Record<string, Asset>;
  assignments: Record<string, Assignment>;
  pointLedger: Record<string, PointLedgerEntry>;
  cardSeries: Record<string, CardSeries>;
  cards: Record<string, Card>;
  childCards: Record<string, ChildCard>;
  achievementRules: Record<string, AchievementRule>;
  rewards: Record<string, Reward>;
  redemptions: Record<string, Redemption>;
  checkins: Record<string, Checkin>;
}

/** 实体表名（与 /v1/sync 的 data 键一致，camelCase） */
export type EntityKind = keyof Entities;
/** 表名 → 实体类型，例如 EntityOf<"contents"> = Content */
export type EntityOf<K extends EntityKind> = Entities[K][string];

export const ENTITY_KINDS: EntityKind[] = [
  "children",
  "scheduleEvents",
  "reminderRules",
  "reminderLogs",
  "folders",
  "tags",
  "contents",
  "assets",
  "assignments",
  "pointLedger",
  "cardSeries",
  "cards",
  "childCards",
  "achievementRules",
  "rewards",
  "redemptions",
  "checkins",
];

export interface SyncStatus {
  syncing: boolean;
  lastSyncAt: number | null;
  lastError: string | null;
  /** 实时通道：connecting / open / closed */
  realtime: "connecting" | "open" | "closed";
}

export interface DataState extends Entities {
  seq: number;
  family: Family | null;
  me: Parent | null;
  members: FamilyMember[];
  /** GET /devices（未吊销的设备） */
  devices: Device[];
  /** 实时通道 presence 推送 + GET family/presence */
  onlineDeviceIds: string[];
  /** 日程展开缓存，键为 "孩子ID|日期" */
  agenda: Record<string, AgendaDay>;
  /** 从 IndexedDB 读过本地快照（不论有没有数据） */
  hydrated: boolean;
  status: SyncStatus;
}

function emptyEntities(): Entities {
  return {
    children: {},
    scheduleEvents: {},
    reminderRules: {},
    reminderLogs: {},
    folders: {},
    tags: {},
    contents: {},
    assets: {},
    assignments: {},
    pointLedger: {},
    cardSeries: {},
    cards: {},
    childCards: {},
    achievementRules: {},
    rewards: {},
    redemptions: {},
    checkins: {},
  };
}

function initialState(): DataState {
  return {
    ...emptyEntities(),
    seq: 0,
    family: null,
    me: null,
    members: [],
    devices: [],
    onlineDeviceIds: [],
    agenda: {},
    hydrated: false,
    status: { syncing: false, lastSyncAt: null, lastError: null, realtime: "closed" },
  };
}

export const dataStore = createStore<DataState>(initialState());

/** 页面订阅数据：const kids = useStore((s) => s.children) */
export function useStore<T>(selector: (s: DataState) => T, isEqual?: (a: T, b: T) => boolean): T {
  return useStoreOf(dataStore, selector, isEqual);
}

// ---------- 修改 ----------

const SNAPSHOT_KEY = "data";
const SNAPSHOT_VERSION = 1;
/** 不写进 IndexedDB 的字段 */
type Snapshot = Omit<DataState, "hydrated" | "status" | "onlineDeviceIds"> & { v: number };

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let persistEnabled = true;

function scheduleSave(): void {
  if (!persistEnabled) return;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    void saveNow();
  }, 500);
}

export async function saveNow(): Promise<void> {
  const { hydrated: _h, status: _s, onlineDeviceIds: _o, ...rest } = dataStore.getState();
  const snap: Snapshot = { ...rest, v: SNAPSHOT_VERSION };
  await idb.set(SNAPSHOT_KEY, snap);
}

/** 启动时读取本地快照（离线也能先显示上次的数据） */
export async function hydrate(): Promise<void> {
  const snap = await idb.get<Snapshot>(SNAPSHOT_KEY);
  if (snap && snap.v === SNAPSHOT_VERSION) {
    const { v: _v, ...rest } = snap;
    dataStore.setState({ ...emptyEntities(), ...rest, hydrated: true });
  } else {
    dataStore.setState({ hydrated: true });
  }
}

function update(patch: Partial<DataState> | ((s: DataState) => Partial<DataState>), persist = true): void {
  dataStore.setState(patch);
  if (persist) scheduleSave();
}

function mergeRows<T extends { id: string; deletedAt?: number | null }>(table: Record<string, T>, rows: T[] | undefined): Record<string, T> {
  if (!rows || rows.length === 0) return table;
  const next = { ...table };
  for (const r of rows) {
    if (r.deletedAt != null) delete next[r.id];
    else next[r.id] = r;
  }
  return next;
}

/** 合并 /v1/sync 的响应：full=true 时先清空；带 deleted_at 的行删除。返回是否有变化 */
export function applySync(resp: SyncResponse): boolean {
  const s = dataStore.getState();
  const base: Entities = resp.full ? emptyEntities() : s;
  const patch: Partial<DataState> = {};
  let changed = resp.full || resp.seq > s.seq;
  for (const kind of ENTITY_KINDS) {
    const rows = resp.data[kind] as { id: string; deletedAt?: number | null }[] | undefined;
    if (rows && rows.length) changed = true;
    (patch as Record<string, unknown>)[kind] = mergeRows(base[kind] as Record<string, { id: string; deletedAt?: number | null }>, rows);
  }
  patch.seq = resp.full ? resp.seq : Math.max(s.seq, resp.seq);
  if (changed) update(patch);
  return changed;
}

/** 写操作成功后，把服务端返回的实体先放进本地（不等下一次同步） */
export function upsert<K extends EntityKind>(kind: K, row: EntityOf<K>): void {
  update((s) => {
    const table = { ...(s[kind] as Record<string, EntityOf<K>>) };
    if (row.deletedAt != null) delete table[row.id];
    else table[row.id] = row;
    return { [kind]: table } as Partial<DataState>;
  });
}

/** 删除成功后先从本地移除 */
export function removeLocal(kind: EntityKind, id: string): void {
  update((s) => {
    const table = { ...(s[kind] as Record<string, unknown>) };
    delete table[id];
    return { [kind]: table } as Partial<DataState>;
  });
}

export function agendaKey(childId: string, date: string): string {
  return `${childId}|${date}`;
}

export function setAgenda(childId: string, days: AgendaDay[]): void {
  update((s) => {
    const agenda = { ...s.agenda };
    for (const d of days) agenda[agendaKey(childId, d.date)] = d;
    return { agenda };
  });
}

export function setFamilyInfo(info: { family: Family; me: Parent; members: FamilyMember[] }): void {
  update(info);
}

export function setDevices(devices: Device[]): void {
  update({ devices });
}

export function setOnlineDeviceIds(ids: string[]): void {
  update({ onlineDeviceIds: ids }, false);
}

export function setSyncStatus(patch: Partial<SyncStatus>): void {
  update((s) => ({ status: { ...s.status, ...patch } }), false);
}

/** 退出登录 / 换家庭时清空内存和 IndexedDB */
export async function wipe(): Promise<void> {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  const status = dataStore.getState().status;
  dataStore.setState({ ...initialState(), hydrated: true, status });
  await idb.clear();
}

/** 测试用：关闭持久化 */
export function _disablePersistence(): void {
  persistEnabled = false;
}

/** 某天要做的一件事：todo 待做 / doing 进行中 / pending 做完等确认 / done 完成（points 是确认后加的分） */
export interface Todo {
  item: AgendaItem;
  done: boolean;
  status: "todo" | "doing" | "pending" | "done";
  points?: number;
}

// ---------- 常用查询（纯函数，在 selector 里用；返回新数组时配合 shallowEqual） ----------

export const select = {
  /** 孩子按名字排序 */
  sortedChildren: (s: DataState): Child[] => Object.values(s.children).sort((a, b) => a.name.localeCompare(b.name, "zh-CN")),
  agendaDay: (s: DataState, childId: string, date: string): AgendaDay | undefined => s.agenda[agendaKey(childId, date)],
  eventsOf: (s: DataState, childId: string): ScheduleEvent[] => Object.values(s.scheduleEvents).filter((e) => e.childId === childId),
  assignmentsOf: (s: DataState, childId: string): Assignment[] => Object.values(s.assignments).filter((a) => a.childId === childId),
  /**
   * 某天要做的事（isTodo，跳过的学习不算）和状态：学习内容看学习记录，其它看孩子点没点"做完了"（家长驳回的不算）。
   * done = 孩子做完了（含等确认）。与 iOS LocalStore.todos 一致
   */
  todos: (s: Pick<DataState, "agenda" | "assignments" | "checkins">, childId: string, date: string): Todo[] => {
    const out: Todo[] = [];
    const checkinOf = (key: string | null) => (key ? Object.values(s.checkins).find((c) => c.occurrenceKey === key) : undefined);
    for (const item of s.agenda[agendaKey(childId, date)]?.items ?? []) {
      if (!isTodo(item)) continue;
      const ck = checkinOf(checkinKeyOf(item, date));
      const pending = ck?.status === "pending";
      if (isContentItem(item)) {
        const a = item.assignmentIds[0] ? s.assignments[item.assignmentIds[0]] : undefined;
        if (a?.status === "skipped") continue;
        const done = !!a && isDone(a);
        out.push({ item, done, status: pending ? "pending" : done ? "done" : a?.status === "in_progress" ? "doing" : "todo" });
        continue;
      }
      const done = !!ck && ck.status !== "rejected";
      out.push({ item, done, status: pending ? "pending" : done ? "done" : "todo", points: done && !pending ? (ck?.awardedPoints ?? 0) : undefined });
    }
    return out;
  },
  rulesOf: (s: DataState, childId: string): ReminderRule[] => Object.values(s.reminderRules).filter((r) => r.childId === childId),
  /** 积分流水，新的在前 */
  ledgerOf: (s: DataState, childId: string): PointLedgerEntry[] =>
    Object.values(s.pointLedger)
      .filter((e) => e.childId === childId)
      .sort((a, b) => b.createdAt - a.createdAt),
  /** 卡牌 ID → 拥有张数 */
  ownedCardCounts: (s: DataState, childId: string): Record<string, number> => {
    const m: Record<string, number> = {};
    for (const cc of Object.values(s.childCards)) if (cc.childId === childId) m[cc.cardId] = (m[cc.cardId] ?? 0) + 1;
    return m;
  },
  sortedSeries: (s: DataState): CardSeries[] =>
    Object.values(s.cardSeries).sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.name.localeCompare(b.name, "zh-CN")),
  /** 图鉴系列（不含成长故事） */
  collectionSeries: (s: DataState): CardSeries[] => select.sortedSeries(s).filter((x) => x.kind !== "story"),
  /** 孩子的成长记录：每次发卡一条，新的在前（按事情发生的日期，再按发卡时间） */
  cardRecords: (s: DataState, childId: string): { record: ChildCard; card: Card }[] =>
    Object.values(s.childCards)
      .filter((cc) => cc.childId === childId && s.cards[cc.cardId])
      .map((record) => ({ record, card: s.cards[record.cardId]! }))
      .sort((a, b) => (b.record.occurredOn ?? "").localeCompare(a.record.occurredOn ?? "") || b.record.obtainedAt - a.record.obtainedAt),
  cardsOf: (s: DataState, seriesId: string): Card[] =>
    Object.values(s.cards)
      .filter((c) => c.seriesId === seriesId)
      .sort((a, b) => a.no - b.no),
  /** 学生设备（未吊销），可按孩子过滤 */
  studentDevices: (s: DataState, childId?: string | null): Device[] =>
    s.devices.filter((d) => d.kind === "student" && d.revokedAt == null && (childId == null || d.childId === childId)),
};
